import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Lock } from "lucide-react";
import { formStateFor } from "@/modules/identity/commands/authCopy";
import {
  formatLebanesePhone,
  safeNextPath,
} from "@/modules/identity/surfaces";
import { Banner, Field } from "@/design/ui";
import { AuthShell } from "~/components/AuthShell";
import { BrowseAsGuest, FootLink, SubmitButton } from "~/components/forms";
import { useMe } from "~/auth/MeProvider";
import { adoptSession } from "~/auth/supabase";
import { post } from "~/lib/api";
import { useForm } from "~/lib/useForm";

/**
 * LogInScreen (docs/ux/screens/LogInScreen.md), consumer phone + password (H22).
 *
 * **The session arrives in the body and supabase-js takes it from there.** The
 * Next host set a cookie and redirected; here the reply carries the tokens, the
 * client adopts them, and `/me` is re-read before the navigation so the screen
 * it lands on already knows who is looking at it. Adopting first and navigating
 * second is the whole ordering: the other way round renders the destination as
 * a guest for one frame and bounces it straight back here.
 */
interface LogInResult {
  verificationRequired: boolean;
  phone?: string;
  session: { accessToken: string; refreshToken: string } | null;
}

export function LogInScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { reload } = useMe();
  const { state, pending, submit } = useForm();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");

  const verified = params.get("verified") === "1";
  // Where to return after a successful login (the /bookings a guest was sent
  // here from). Sanitized by the server's own guard; ignored otherwise.
  const next = safeNextPath(params.get("next")) ?? "/";

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const result = await submit<LogInResult>(
      () => post("/auth/login", { phone, password }),
      formStateFor,
      { phone },
    );
    if (!result) return;

    if (result.verificationRequired) {
      const query = new URLSearchParams({
        phone: result.phone ?? phone,
        unverified: "1",
      });
      void navigate(`/verify?${query.toString()}`);
      return;
    }
    if (result.session) {
      await adoptSession(result.session);
      await reload();
    }
    void navigate(next, { replace: true });
  }

  return (
    <AuthShell>
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-ink">Welcome back</h1>
        <p className="mt-1 text-sm text-muted">
          Log in to manage your appointments
        </p>
      </div>

      {verified && (
        <Banner tone="success">Your account is verified, log in below</Banner>
      )}
      {state.formError && <Banner>{state.formError}</Banner>}

      <form onSubmit={onSubmit}>
        <fieldset disabled={pending} className="disabled:opacity-70">
          <Field
            label="Phone number"
            name="phone"
            type="tel"
            inputMode="tel"
            placeholder="71 234 456"
            prefix="+961"
            value={phone}
            onChange={(v) => setPhone(formatLebanesePhone(v))}
            error={state.fieldErrors?.phone}
            autoComplete="tel"
          />
          <Field
            label="Password"
            name="password"
            type="password"
            value={password}
            onChange={setPassword}
            error={state.fieldErrors?.password}
            autoComplete="current-password"
            icon={Lock}
          />
          <SubmitButton pending={pending} pendingLabel="Logging in…">
            Log in
          </SubmitButton>
        </fieldset>
      </form>

      <FootLink lead="New here?" to="/signup" className="mt-6">
        Sign up
      </FootLink>
      <FootLink lead="Business or staff?" to="/login/staff">
        Log in here
      </FootLink>
      <BrowseAsGuest />
    </AuthShell>
  );
}
